import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useEffect, useState } from 'react';

import { logError } from '@/modules/analytics';
import { getPayingOrganization } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

import { IneligibleTemplate } from './components/IneligibleTemplate';

/**
 * "No program" dead-end, consolidating Sparta's EmptyProgramContainer +
 * EmptyProgramPage. Shown both when eligibility lands on `Eligible_User_Not_Qualified`
 * and when the post-survey program catalog comes back empty (resolveOnboardingRoute's
 * `requiresCrisisLine`-less empty branch). If the matched paying org has a custom
 * `fallbackMessage` (admin-dash configured), render it as markdown; otherwise the
 * default "don't currently qualify" copy. The message comes from the user's
 * payingOrganization when present (the post-survey case always has one), else a
 * fetch of the matched org by its `webserverOrgUuid` (the eligibility case often
 * has no payingOrganization yet). Renders through the shared IneligibleTemplate.
 */
export function EmptyProgramScreen() {
  const t = useT();
  const { user, selectedOrg } = useRulesEngine();

  const userFallback = user?.payingOrganization?.fallbackMessage;
  const webserverOrgUuid = selectedOrg?.webserverOrgUuid;

  const [fallbackMessage, setFallbackMessage] = useState<string | null | undefined>(userFallback);
  // Only fetch (and show the spinner) when we don't already have a message and
  // there's an org to look it up by.
  const [isLoading, setIsLoading] = useState(!userFallback && Boolean(webserverOrgUuid));

  useEffect(() => {
    if (userFallback || !webserverOrgUuid) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getPayingOrganization(webserverOrgUuid)
      .then((org) => {
        if (!cancelled) setFallbackMessage(org?.fallbackMessage);
      })
      .catch((err: unknown) => {
        // Fall through to the default copy, but log so a systemic org-lookup
        // outage is observable rather than silently swallowed.
        logError('EmptyProgram: failed to fetch paying organization fallback message', {
          error: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userFallback, webserverOrgUuid]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (fallbackMessage) {
    return <IneligibleTemplate markdown={fallbackMessage} />;
  }

  return (
    <IneligibleTemplate
      title={t("Sorry, you don't currently qualify for any programs")}
      body={
        <VStack space='xl' className='items-center'>
          <Text className='text-center text-typography-700'>
            <T _str='Based on the information provided, you do not currently qualify for any of our health programs at Vida.' />
          </Text>
          <Text className='text-center text-typography-700'>
            <T _str='We encourage you to continue prioritizing your well-being. Your healthcare provider can work with you to develop a personalized plan that aligns with your needs.' />
          </Text>
        </VStack>
      }
    />
  );
}
